import { useState, useMemo, useCallback, useEffect } from "react";
import * as Tabs from "@radix-ui/react-tabs";
import { useIsLoggedIn, useDynamicContext } from "@dynamic-labs/sdk-react-core";
import { useQueryClient } from "@tanstack/react-query";

import { Header } from "./components/Header";
import { LandingPage } from "./components/LandingPage";
import { SendPanel } from "./components/SendPanel";
import { SwapPanel } from "./components/SwapPanel";
import { TokenSelectDialog, PasteAddressDialog } from "./components/TokenSelectDialog";
import { ConfirmSendDialog } from "./components/ConfirmSendDialog";
import { ConfirmSwapDialog } from "./components/ConfirmSwapDialog";
import { QuestionIcon } from "./components/Icons";

import { useTokenList, mergeTokensWithBalances } from "./hooks/useTokenList";
import { useBalance } from "./hooks/useBalance";
import { useJupiterQuote } from "./hooks/useJupiterQuote";
import { useDebounce } from "./hooks/useDebounce";
import { useSendFeeEstimate } from "./hooks/useSendFeeEstimate";
import { useSwapFeeEstimate } from "./hooks/useSwapFeeEstimate";

import type { JupiterQuote, Token } from "./types";
import { SOL_ADDRESS, USDC_MINT, tokenAmountToRaw } from "./lib/utils";

const DEFAULT_SEND_TOKEN = SOL_ADDRESS;
const DEFAULT_SWAP_FROM = SOL_ADDRESS;
const DEFAULT_SWAP_TO = USDC_MINT;

type TabMode = "send" | "swap";
type PickerTarget = "send" | "swapFrom" | "swapTo";
type SendConfirmSnapshot = {
	token: Token;
	amount: number;
	recipient: string;
	feeSol?: number;
};
type SwapConfirmSnapshot = {
	fromToken: Token;
	toToken: Token;
	fromAmount: number;
	quote: JupiterQuote;
};

interface AppProps {
	theme: "light" | "dark";
	onThemeChange: (t: "light" | "dark") => void;
}

type AppScreen = "landing" | "demo";

export function App({ theme, onThemeChange }: AppProps) {
	const [screen, setScreen] = useState<AppScreen>(() =>
		window.location.hash === "#demo" ? "demo" : "landing",
	);

	useEffect(() => {
		const syncScreen = () => {
			setScreen(window.location.hash === "#demo" ? "demo" : "landing");
		};

		window.addEventListener("hashchange", syncScreen);
		window.addEventListener("popstate", syncScreen);

		return () => {
			window.removeEventListener("hashchange", syncScreen);
			window.removeEventListener("popstate", syncScreen);
		};
	}, []);

	const openDemo = useCallback(() => {
		setScreen("demo");
		if (window.location.hash !== "#demo") {
			window.history.pushState(null, "", "#demo");
		}
		window.scrollTo({ top: 0, behavior: "smooth" });
	}, []);

	const openLanding = useCallback(() => {
		setScreen("landing");
		window.history.pushState(null, "", `${window.location.pathname}${window.location.search}`);
		window.scrollTo({ top: 0, behavior: "smooth" });
	}, []);

	if (screen === "landing") {
		return (
			<LandingPage theme={theme} onThemeChange={onThemeChange} onOpenDemo={openDemo} />
		);
	}

	return <DemoExperience theme={theme} onThemeChange={onThemeChange} onHome={openLanding} />;
}

interface DemoExperienceProps extends AppProps {
	onHome: () => void;
}

function DemoExperience({ theme, onThemeChange, onHome }: DemoExperienceProps) {
	const [tab, setTab] = useState<TabMode>("send");

	const [sendTokenAddr, setSendTokenAddr] = useState(DEFAULT_SEND_TOKEN);
	const [sendAmount, setSendAmount] = useState("");
	const [recipient, setRecipient] = useState("");

	const [swapFromAddr, setSwapFromAddr] = useState(DEFAULT_SWAP_FROM);
	const [swapToAddr, setSwapToAddr] = useState(DEFAULT_SWAP_TO);
	const [swapFromAmount, setSwapFromAmount] = useState("");

	const [pickerOpen, setPickerOpen] = useState(false);
	const [pickerTarget, setPickerTarget] = useState<PickerTarget>("send");
	const [addressOpen, setAddressOpen] = useState(false);
	const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
	const [sendConfirmSnapshot, setSendConfirmSnapshot] = useState<SendConfirmSnapshot | null>(null);
	const [swapConfirmOpen, setSwapConfirmOpen] = useState(false);
	const [swapConfirmSnapshot, setSwapConfirmSnapshot] = useState<SwapConfirmSnapshot | null>(null);

	const isLoggedIn = useIsLoggedIn();
	const { primaryWallet } = useDynamicContext();
	const walletAddress = primaryWallet?.address;

	const queryClient = useQueryClient();

	const { data: rawTokens, isLoading: isTokenListLoading } = useTokenList();
	const { data: balances, isLoading: isBalanceLoading } = useBalance(walletAddress);

	const tokens: Token[] = useMemo(() => {
		if (!rawTokens) return [];
		return mergeTokensWithBalances(rawTokens, balances ?? {});
	}, [rawTokens, balances]);

	const findToken = useCallback(
		(addr: string) => tokens.find((t) => t.address === addr),
		[tokens],
	);

	const sendToken = findToken(sendTokenAddr);
	const swapFromToken = findToken(swapFromAddr);
	const swapToToken = findToken(swapToAddr);

	const sendAmtNum = parseFloat(sendAmount) || 0;
	const swapFromAmtNum = parseFloat(swapFromAmount) || 0;

	const debouncedSendAmount = useDebounce(sendAmtNum, 600);

	const { data: sendFeeData, isLoading: isSendFeeLoading } = useSendFeeEstimate({
		fromAddress: walletAddress,
		toAddress: recipient || undefined,
		tokenAddress: sendToken?.address,
		amount: debouncedSendAmount,
		decimals: sendToken?.decimals ?? 9,
		enabled:
			tab === "send" &&
			debouncedSendAmount > 0 &&
			Boolean(recipient) &&
			Boolean(sendToken),
	});

	const swapAmountRaw =
		swapFromToken && swapFromAmtNum > 0
			? tokenAmountToRaw(swapFromAmtNum, swapFromToken.decimals)
			: 0n;

	const {
		data: jupiterQuote,
		isLoading: isQuoteLoading,
		refetch: refetchQuote,
	} = useJupiterQuote({
		inputMint: swapFromAddr,
		outputMint: swapToAddr,
		amount: swapAmountRaw,
		decimals: swapFromToken?.decimals ?? 9,
		enabled: tab === "swap" && swapAmountRaw > 0n && swapFromAddr !== swapToAddr,
	});

	const { data: swapFeeData, isLoading: isSwapFeeLoading } = useSwapFeeEstimate({
		fromToken: swapFromToken,
		toToken: swapToToken,
		fromAmount: swapFromAmtNum,
		quote: jupiterQuote,
		takerAddress: walletAddress,
		enabled: tab === "swap" && Boolean(jupiterQuote),
	});

	const openPicker = (target: PickerTarget) => {
		setPickerTarget(target);
		setPickerOpen(true);
	};

	const handlePickToken = (tok: Token) => {
		if (pickerTarget === "send") {
			setSendTokenAddr(tok.address);
			setSendAmount("");
		} else if (pickerTarget === "swapFrom") {
			setSwapFromAddr(tok.address);
			setSwapFromAmount("");
		} else if (pickerTarget === "swapTo") {
			setSwapToAddr(tok.address);
		}
		setPickerOpen(false);
	};

	const flipSwap = () => {
		setSwapFromAddr(swapToAddr);
		setSwapToAddr(swapFromAddr);
		setSwapFromAmount("");
	};

	const refreshBalances = useCallback(() => {
		void queryClient.invalidateQueries({ queryKey: ["balance", walletAddress] });
	}, [queryClient, walletAddress]);

	const openSendConfirm = () => {
		if (!sendToken || sendAmtNum <= 0 || !recipient) return;
		setSendConfirmSnapshot({
			token: sendToken,
			amount: sendAmtNum,
			recipient,
			feeSol: sendFeeData?.feeSol,
		});
		setSendConfirmOpen(true);
	};

	const handleSendConfirmOpenChange = (nextOpen: boolean) => {
		setSendConfirmOpen(nextOpen);
		if (!nextOpen) {
			setSendConfirmSnapshot(null);
		}
	};

	const openSwapConfirm = () => {
		if (!swapFromToken || !swapToToken || !jupiterQuote) return;
		setSwapConfirmSnapshot({
			fromToken: swapFromToken,
			toToken: swapToToken,
			fromAmount: swapFromAmtNum,
			quote: jupiterQuote,
		});
		setSwapConfirmOpen(true);
	};

	const handleSwapConfirmOpenChange = (nextOpen: boolean) => {
		setSwapConfirmOpen(nextOpen);
		if (!nextOpen) {
			setSwapConfirmSnapshot(null);
		}
	};

	const currentPickerAddr =
		pickerTarget === "send"
			? sendTokenAddr
			: pickerTarget === "swapFrom"
				? swapFromAddr
				: swapToAddr;

	const otherPickerAddr =
		pickerTarget === "swapFrom"
			? swapToAddr
			: pickerTarget === "swapTo"
				? swapFromAddr
				: undefined;

	return (
		<div className="app">
			<Header theme={theme} onThemeToggle={onThemeChange} onHome={onHome} />

			<main className="main">
				<div className="swap-card-wrap">
					<h1 className="send-onboarding-copy">
						<span className="send-onboarding-main">Frictionless onboarding.</span>
						<span className="send-onboarding-accent">"Zero SOL" required.</span>
					</h1>

					<Tabs.Root value={tab} onValueChange={(v) => setTab(v as TabMode)}>
						<div className="card-head">
							<Tabs.List className="tabs-list" aria-label="Mode">
								<Tabs.Trigger className="tab-trigger" value="send">
									Send
								</Tabs.Trigger>
								<Tabs.Trigger className="tab-trigger" value="swap">
									Swap
								</Tabs.Trigger>
							</Tabs.List>
						</div>

						<Tabs.Content value="send" tabIndex={-1}>
							<SendPanel
								token={sendToken}
								tokens={tokens}
								amount={sendAmount}
								recipient={recipient}
								isBalanceLoading={isBalanceLoading || isTokenListLoading}
								isFeeLoading={isSendFeeLoading}
								feeSol={sendFeeData?.feeSol}
								walletConnected={isLoggedIn && Boolean(walletAddress)}
								onAmountChange={setSendAmount}
								onPickToken={() => openPicker("send")}
								onPickRecipient={() => setAddressOpen(true)}
								onSubmit={openSendConfirm}
							/>
						</Tabs.Content>

						<Tabs.Content value="swap" tabIndex={-1}>
							<SwapPanel
								fromToken={swapFromToken}
								toToken={swapToToken}
								fromAmount={swapFromAmount}
								quote={jupiterQuote}
								isQuoteLoading={isQuoteLoading}
								isFeeLoading={isSwapFeeLoading}
								feeSol={swapFeeData?.feeSol}
								isBalanceLoading={isBalanceLoading || isTokenListLoading}
								walletConnected={isLoggedIn && Boolean(walletAddress)}
								onFromAmountChange={setSwapFromAmount}
								onPickFrom={() => openPicker("swapFrom")}
								onPickTo={() => openPicker("swapTo")}
								onFlip={flipSwap}
								onRefreshQuote={() => void refetchQuote()}
								onSubmit={openSwapConfirm}
							/>
						</Tabs.Content>
					</Tabs.Root>
				</div>
			</main>

			<button className="support-fab" aria-label="Help">
				<QuestionIcon />
			</button>

			<TokenSelectDialog
				open={pickerOpen}
				onOpenChange={setPickerOpen}
				tokens={tokens}
				isLoading={isTokenListLoading}
				currentAddress={currentPickerAddr}
				otherAddress={otherPickerAddr}
				onPick={handlePickToken}
			/>

			<PasteAddressDialog
				open={addressOpen}
				onOpenChange={setAddressOpen}
				initial={recipient}
				onConfirm={(addr) => {
					setRecipient(addr);
					setAddressOpen(false);
				}}
			/>

			<ConfirmSendDialog
				open={sendConfirmOpen}
				onOpenChange={handleSendConfirmOpenChange}
				token={sendConfirmSnapshot?.token}
				amount={sendConfirmSnapshot?.amount ?? 0}
				recipient={sendConfirmSnapshot?.recipient ?? ""}
				feeSol={sendConfirmSnapshot?.feeSol ?? sendFeeData?.feeSol}
				isFeeLoading={isSendFeeLoading}
				onSuccess={() => {
					setSendAmount("");
					refreshBalances();
				}}
			/>

			<ConfirmSwapDialog
				open={swapConfirmOpen}
				onOpenChange={handleSwapConfirmOpenChange}
				fromToken={swapConfirmSnapshot?.fromToken}
				toToken={swapConfirmSnapshot?.toToken}
				fromAmount={swapConfirmSnapshot?.fromAmount ?? 0}
				quote={swapConfirmSnapshot?.quote}
				feeSol={swapFeeData?.feeSol}
				isFeeLoading={isSwapFeeLoading}
				onSuccess={() => {
					setSwapFromAmount("");
					refreshBalances();
				}}
			/>
		</div>
	);
}
